import { useCallback, useEffect, useRef, useState } from 'react'
import { isEmpty } from 'lodash-es'
import {
  isSameRouteKey,
  planEnterCreateModeAfterDiscard,
  resolveRouteSelectionSync,
  type RouteSelectionChangeMeta,
} from './createModePolicy'
import { shouldWarnOnPlannerModeSwitch } from './unsavedChangesGate'
import type { EditState, InteractionMode, PathState, RouteId } from './types'

type UsePlannerModeArgs = {
  initialRoute?: RouteId
  initialCreateMode?: boolean
  returnRouteAfterCreate?: RouteId
  interactionMode: InteractionMode
  setInteractionMode: (mode: InteractionMode) => void
  selectedRoute: RouteId | undefined
  setSelectedRoute: (route: RouteId | undefined) => void
  existingRoutes: RouteId[]
  editState: EditState | null
  pathState: PathState
  hasUnsavedChanges: boolean
  suppressUnsavedFlag: () => void
  resetPathHistory: () => void
  onRouteSelectionChange?: (route: RouteId | undefined, meta?: RouteSelectionChangeMeta) => void
  onClearTransient: () => void
}

/**
 * Mode-switch policy and navigation side effects. The mode itself lives in the planner
 * core; `setInteractionMode` dispatches `SET_MODE`, which also cancels edits and clears
 * transients/history per mode rules — this hook only decides when a switch needs a
 * confirmation dialog and how it interacts with route selection and the URL.
 */
export function usePlannerMode({
  initialRoute,
  initialCreateMode = false,
  returnRouteAfterCreate,
  interactionMode,
  setInteractionMode,
  selectedRoute,
  setSelectedRoute,
  existingRoutes,
  editState,
  pathState,
  hasUnsavedChanges,
  suppressUnsavedFlag,
  resetPathHistory,
  onRouteSelectionChange,
  onClearTransient,
}: UsePlannerModeArgs) {
  const [modeSwitchConfirmOpen, setModeSwitchConfirmOpen] = useState(false)
  const [pendingMode, setPendingMode] = useState<InteractionMode | null>(null)
  const deepLinkedInitialRef = useRef(false)
  const routeBeforeCreateRef = useRef<RouteId | undefined>(returnRouteAfterCreate)

  const isPathBuildMode = interactionMode === 'edit' || interactionMode === 'create'
  const isDeleteMode = interactionMode === 'delete'

  useEffect(() => {
    if (initialCreateMode) setInteractionMode('create')
  }, [initialCreateMode, setInteractionMode])

  useEffect(() => {
    if (deepLinkedInitialRef.current) return
    if (!initialRoute) return
    deepLinkedInitialRef.current = true
    onRouteSelectionChange?.(initialRoute)
  }, [initialRoute, onRouteSelectionChange])

  const applyInteractionMode = useCallback(
    (mode: InteractionMode) => {
      if (mode === 'create') {
        const previous = selectedRoute ?? routeBeforeCreateRef.current
        const plan = planEnterCreateModeAfterDiscard(previous)
        routeBeforeCreateRef.current = previous
        suppressUnsavedFlag()
        setInteractionMode(plan.interactionMode)
        if (plan.clearSelectedRoute) setSelectedRoute(undefined)
        onRouteSelectionChange?.(undefined, {
          mode: 'create',
          returnRoute: previous,
          ignoreBlocker: plan.ignoreBlocker,
        })
        return
      }

      if (mode === 'edit' && interactionMode === 'create' && !selectedRoute) {
        const previous = routeBeforeCreateRef.current
        setInteractionMode(mode)
        if (previous) {
          setSelectedRoute(previous)
          onRouteSelectionChange?.(previous, { ignoreBlocker: true })
        } else {
          onRouteSelectionChange?.(undefined, { ignoreBlocker: true })
        }
        return
      }

      setInteractionMode(mode)
    },
    [interactionMode, selectedRoute, setInteractionMode, setSelectedRoute, onRouteSelectionChange, suppressUnsavedFlag]
  )

  const requestInteractionMode = useCallback(
    (mode: InteractionMode) => {
      if (mode === interactionMode) return
      const dirty = shouldWarnOnPlannerModeSwitch({
        fromMode: interactionMode,
        toMode: mode,
        hasUnsavedChanges,
        hasEditState: Boolean(editState),
        createModeHasPath: interactionMode === 'create' && !isEmpty(pathState.segments),
      })
      const clearingForCreate =
        mode === 'create' && (Boolean(selectedRoute) || !isEmpty(pathState.segments) || Boolean(editState))
      if (dirty || clearingForCreate) {
        setPendingMode(mode)
        setModeSwitchConfirmOpen(true)
        return
      }
      applyInteractionMode(mode)
    },
    [interactionMode, hasUnsavedChanges, editState, pathState.segments, selectedRoute, applyInteractionMode]
  )

  const handleSelectRoute = useCallback(
    (route: RouteId | undefined, opts?: { forceSync?: boolean }) => {
      // Re-selecting the loaded route only re-frames the camera — it must not
      // wipe undo history or transients.
      if (!isSameRouteKey(route, selectedRoute)) {
        resetPathHistory()
        onClearTransient()
      }
      setSelectedRoute(route)

      const listed = route ? existingRoutes.some(r => r.id === route.id && r.version === route.version) : false
      const sync = resolveRouteSelectionSync({
        route,
        interactionMode,
        isListed: listed,
        forceSync: opts?.forceSync,
      })

      if (sync.action === 'navigate') {
        onRouteSelectionChange?.(sync.route)
        if (interactionMode === 'create') setInteractionMode('edit')
      } else if (sync.action === 'clear') {
        onRouteSelectionChange?.(undefined, {
          mode: sync.createMode ? 'create' : undefined,
          returnRoute: sync.createMode ? routeBeforeCreateRef.current : undefined,
          ignoreBlocker: sync.createMode,
        })
      }
    },
    [
      selectedRoute,
      setSelectedRoute,
      existingRoutes,
      onRouteSelectionChange,
      interactionMode,
      setInteractionMode,
      resetPathHistory,
      onClearTransient,
    ]
  )

  const confirmModeSwitch = useCallback(() => {
    const mode = pendingMode
    setModeSwitchConfirmOpen(false)
    setPendingMode(null)
    if (mode) applyInteractionMode(mode)
  }, [pendingMode, applyInteractionMode])

  const cancelModeSwitch = useCallback(() => {
    setModeSwitchConfirmOpen(false)
    setPendingMode(null)
  }, [])

  return {
    interactionMode,
    isPathBuildMode,
    isDeleteMode,
    requestInteractionMode,
    applyInteractionMode,
    handleSelectRoute,
    modeSwitchConfirmOpen,
    pendingMode,
    confirmModeSwitch,
    cancelModeSwitch,
  }
}
